import Link from 'next/link';
import { useTranslations } from 'next-intl';

import AppStoreIcon from '@/fo/icons/base/apps/app-store.svg';
import GooglePlayIcon from '@/fo/icons/base/apps/google-play.svg';
import MicrophoneIcon from '@/fo/icons/base/icons/microphone.svg';
import BiodermaLogo from '@/fo/icons/base/logos/bioderma.svg';
import EsthedermLogo from '@/fo/icons/base/logos/esthederm.svg';
import EtatPurLogo from '@/fo/icons/base/logos/etat-pur.svg';
import NaosLogo from '@/fo/icons/base/logos/naos.svg';
import DzenIcon from '@/fo/icons/base/socials/dzen.svg';
import TgIcon from '@/fo/icons/base/socials/telegram.svg';
import VkIcon from '@/fo/icons/base/socials/vk.svg';
import YouTubeIcon from '@/fo/icons/base/socials/youtube.svg';

import { GroupLinks } from '../components/GroupLinks/GroupLinks';

import { type FooterData } from './types';
import { styles } from './UiAppFooterBase.styles';

const logoMap = {
  naos: NaosLogo,
  bioderma: BiodermaLogo,
  esthederm: EsthedermLogo,
  etatPur: EtatPurLogo
};

const linkMap = {
  naos: '/',
  bioderma: '/bioderma/',
  esthederm: '/esthederm/',
  etatPur: '/etat_pur/'
};

const socialMap = {
  vk: VkIcon,
  telegram: TgIcon,
  dzen: DzenIcon,
  youtube: YouTubeIcon,
  tg: TgIcon
};

const appMap = {
  googlePlay: GooglePlayIcon,
  apple: AppStoreIcon
};

export interface UiAppFooterBaseProps {
  data: FooterData;
}

export function UiAppFooterBase({ data }: UiAppFooterBaseProps) {
  const t = useTranslations();

  const slots = styles();
  const { brands, contact, podcast, links, app, copyright } = data;

  const LogoComponent = logoMap[brands.main.slug];

  return (
    <footer
      id="app-footer"
      className={slots.base()}
    >
      <div className={slots.container()}>
        <div className={slots.naosBlock()}>
          <Link href={linkMap.naos}>
            <LogoComponent
              width={252}
              height={56}
              className={slots.naosLogo()}
            />
          </Link>
          <p className={slots.naosDescription()}>{brands.main.title}</p>
        </div>

        <div className={slots.brandsSection()}>
          {brands.others.map((brand) => {
            const BrandLogo = logoMap[brand.slug];

            return (
              <div
                key={brand.slug}
                className={slots.brandBlock()}
              >
                <GroupLinks
                  title={
                    <Link
                      href={linkMap[brand.slug]}
                      className={slots.brandLogoLink()}
                    >
                      <BrandLogo
                        className={slots.brandLogo()}
                        width={100}
                        height={36}
                      />
                    </Link>
                  }
                >
                  <div className={slots.brandLinks()}>
                    <div className={slots.brandLinksGroup()}>
                      <Link
                        href={brand.links.about}
                        className={slots.brandLink()}
                      >
                        {t('about-brand')} {brand.title || brand.slug}
                      </Link>
                      <Link
                        href={brand.links.catalog}
                        className={slots.brandLink()}
                      >
                        {t('catalog-products')}
                      </Link>
                    </div>
                    {brand.social.map((social) => {
                      const SocialIcon = socialMap[social.type];

                      return (
                        <Link
                          href={social.link}
                          key={social.type}
                          target={social.isBlank ? '_blank' : '_self'}
                          rel={
                            social.isBlank
                              ? 'noopener noreferrer nofollow'
                              : undefined
                          }
                          className={slots.socialBlock()}
                        >
                          <div className={slots.socialIcon()}>
                            <SocialIcon
                              width={24}
                              height={24}
                            />
                          </div>
                          <span className={slots.socialText()}>
                            {social.title}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </GroupLinks>
              </div>
            );
          })}
        </div>

        <div className={slots.bottomSection()}>
          <GroupLinks
            title={<span className={slots.bottomTitle()}>{links.title}</span>}
          >
            <div className={slots.bottomLinks()}>
              {links.columns.map((column, index) => (
                <div
                  key={index}
                  className={slots.bottomLinksColumn()}
                >
                  {column.links.map((link) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      className={slots.bottomLink()}
                    >
                      {link.title}
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          </GroupLinks>
        </div>

        <div className={slots.sideColumn()}>
          <div className={slots.contactSection()}>
            <div className={slots.contactWrapper()}>
              <div className={slots.phoneBlock()}>
                <Link
                  href={`tel:${contact.phone}`}
                  className={slots.phoneNumber()}
                >
                  {contact.phone}
                </Link>
                <span className={slots.phoneHours()}>{contact.hours}</span>
              </div>
              <a
                href={`mailto:${contact.email}`}
                className={slots.email()}
              >
                {contact.email}
              </a>
            </div>
            <div className={slots.podcastBlock()}>
              <div className={slots.podcastHeader()}>
                <div className={slots.podcastIcon()}>
                  <MicrophoneIcon
                    width={24}
                    height={24}
                  />
                </div>
                <span className={slots.podcastTitle()}>{podcast.title}</span>
              </div>
              <div className={slots.socialLinks()}>
                {podcast.socials.map((social) => {
                  const SocialIcon = socialMap[social.icon];

                  return (
                    <Link
                      key={social.icon}
                      href={social.link}
                      target={social.isBlank ? '_blank' : '_self'}
                      rel={
                        social.isBlank
                          ? 'noopener noreferrer nofollow'
                          : undefined
                      }
                      className={slots.socialIcon()}
                    >
                      <SocialIcon />
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>

          <div className={slots.appSection()}>
            <div className={slots.appTitleWrapper()}>
              <span className={slots.bottomTitle()}>{app.title}</span>
            </div>
            <div className={slots.appLinks()}>
              {app.stores.map((store) => {
                const StoreIcon = appMap[store.type];

                return (
                  <Link
                    key={store.type}
                    href={store.link}
                    target={store.isBlank ? '_blank' : '_self'}
                    rel={
                      store.isBlank ? 'noopener noreferrer nofollow' : undefined
                    }
                    className={slots.appButton()}
                  >
                    <StoreIcon />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        <div className={slots.copyright()}>
          <div className={slots.copyrightBlock()}>
            {copyright.text.map((text, index) => (
              <span
                key={index}
                className={slots.copyrightText()}
              >
                {text}
              </span>
            ))}
            <span className={slots.copyrightText()}>
              {t.rich('footer.recommendationTechnologies', {
                link: (chunks) => (
                  <Link
                    href="/legal/pravila-primeneniya-rekomendatelnyh-tehnologiy/"
                    className={slots.recommendationLink()}
                  >
                    {chunks}
                  </Link>
                )
              })}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
